/* =============================================================
   NotificationBell.jsx — Admin portal notification bell
   -------------------------------------------------------------
   Two notification sources:

   A. STUDENT NOTIFICATIONS (computed from live DB data)
      1. Pending documents  — 1 notification per student (grouped)
      2. Urgent students    — students flagged is_urgent
      3. Upcoming deadlines — deadline within 14 days
      4. LA not started     — outgoing students with no LA

   B. UNIVERSITY JOIN NOTIFICATIONS (from university_join_notifications table)
      Triggered when a new university publishes their profile for the
      first time (profile_published_at transitions null → non-null).
      Each university sees a notification with "Connect" / "Dismiss".
      Connect logic:
        1. If already linked → just dismiss
        2. If a manual partner name-matches → ask to link to it
        3. Otherwise → add as new Connected partner

   Cache: module-level Map, 2-minute TTL for student notifications.
   Join notifications are always fetched fresh on open.
   ============================================================= */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth }  from '../context/AuthContext';


/* ─── MODULE-LEVEL CACHE (student notifs only) ───────────────── */
const _cache = {
  data:         null,
  universityId: null,
  fetchedAt:    0,
};
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes


/* ─── NOTIFICATION PREFERENCES ──────────────────────────────── */
const NOTIF_PREFS_KEY      = 'unixplore_settings_notif';
const NOTIF_PREFS_DEFAULTS = { documentUploads: true, deadlineReminders: true, newUniversities: true };
function loadNotifPrefs() {
  try { return { ...NOTIF_PREFS_DEFAULTS, ...JSON.parse(localStorage.getItem(NOTIF_PREFS_KEY) || '{}') }; }
  catch { return NOTIF_PREFS_DEFAULTS; }
}


/* ─── READ STATE ─────────────────────────────────────────────── */
const READ_KEY = 'unixplore_notif_read_v2';
function loadRead()    { try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || '[]')); } catch { return new Set(); } }
function saveRead(set) { try { localStorage.setItem(READ_KEY, JSON.stringify([...set])); } catch {} }


/* ─── DATE HELPERS ───────────────────────────────────────────── */
function fmtDate(s) {
  if (!s) return '—';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s)
    ? new Date(s + 'T00:00:00')
    : new Date(s);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function daysUntil(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(dateStr + 'T00:00:00') - today) / 86_400_000);
}


/* ─── STUDENT NOTIFICATION BUILDER ──────────────────────────── */
function buildNotifs(pendingDocs, students) {
  const notifs = [];

  /* 1. Pending documents — grouped 1 notification per student */
  const byStudent = {};
  (pendingDocs ?? []).forEach(doc => {
    const s = doc.students;
    if (!s) return;
    if (!byStudent[s.id]) byStudent[s.id] = { s, docs: [] };
    byStudent[s.id].docs.push(doc);
  });

  Object.values(byStudent).forEach(({ s, docs }) => {
    docs.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
    const title = docs.length === 1
      ? `${s.name}: ${docs[0].doc_name}`
      : `${s.name}: ${docs.length} documents pending`;
    notifs.push({
      id:        `pending-${s.id}`,
      icon:      'folder_open',
      iconBg:    'bg-amber-100',
      iconColor: 'text-amber-700',
      title,
      desc:      `Uploaded ${fmtDate(docs[0].uploaded_at)} · Needs review`,
      route:     `/${s.type}/${s.id}`,
      priority:  80,
    });
  });

  /* 2. Urgent students */
  (students ?? []).filter(s => s.is_urgent).forEach(s => {
    notifs.push({
      id:        `urgent-${s.id}`,
      icon:      'priority_high',
      iconBg:    'bg-red-100',
      iconColor: 'text-red-600',
      title:     `${s.name} requires urgent attention`,
      desc:      `${s.destination_university ?? s.home_university ?? '—'} · ${s.status}`,
      route:     `/${s.type}/${s.id}`,
      priority:  100,
    });
  });

  /* 3. Upcoming deadlines (within 14 days) */
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const in14  = new Date(today.getTime() + 14 * 86_400_000);
  (students ?? []).filter(s => {
    if (!s.deadline) return false;
    const d = new Date(s.deadline + 'T00:00:00');
    return d >= today && d <= in14;
  }).forEach(s => {
    const days = daysUntil(s.deadline);
    notifs.push({
      id:        `deadline-${s.id}`,
      icon:      'event',
      iconBg:    'bg-indigo-100',
      iconColor: 'text-indigo-600',
      title:     `Deadline: ${s.name}`,
      desc:      `${fmtDate(s.deadline)} · ${days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} days`}`,
      route:     `/${s.type}/${s.id}`,
      priority:  days <= 3 ? 90 : 50,
    });
  });

  /* 4. Outgoing with Learning Agreement not started */
  (students ?? [])
    .filter(s =>
      s.type === 'outgoing' &&
      s.learning_agreement_status === 'Not started' &&
      !['Returned', 'Departed'].includes(s.status)
    )
    .forEach(s => {
      notifs.push({
        id:        `la-${s.id}`,
        icon:      'contract_edit',
        iconBg:    'bg-violet-100',
        iconColor: 'text-violet-600',
        title:     `${s.name}: Learning Agreement not started`,
        desc:      `${s.destination_university ?? '—'} · Action required`,
        route:     `/outgoing/${s.id}`,
        priority:  25,
      });
    });

  /* Deduplicate + sort by priority */
  const seen = new Set();
  return notifs
    .filter(n => { if (seen.has(n.id)) return false; seen.add(n.id); return true; })
    .sort((a, b) => b.priority - a.priority);
}


/* ─── COMPONENT ──────────────────────────────────────────────── */
export default function NotificationBell() {
  const navigate = useNavigate();
  const ref      = useRef(null);
  const { user } = useAuth();

  /* ── Student notification state ────────────────────────────── */
  const [open,       setOpen]       = useState(false);
  const leaveTimer = useRef(null);
  const [notifs,   setNotifs]   = useState(_cache.data ?? []);
  const [fetching, setFetching] = useState(false);
  const [readIds,  setReadIds]  = useState(() => loadRead());

  /* ── Join notification state ───────────────────────────────── */
  const [joinNotifs,     setJoinNotifs]     = useState([]);
  // pendingConnect: { notif, match: { id, name } | null }
  // When set, shows a confirmation modal before executing the connect.
  const [pendingConnect, setPendingConnect] = useState(null);
  const [connecting,     setConnecting]     = useState(false);

  // Apply notification preferences from Settings
  const prefs = loadNotifPrefs();
  const visibleNotifs     = notifs.filter(n => {
    if (!prefs.documentUploads   && n.id.startsWith('pending-'))  return false;
    if (!prefs.deadlineReminders && n.id.startsWith('deadline-')) return false;
    if (!prefs.deadlineReminders && n.id.startsWith('urgent-'))   return false;
    return true;
  });
  const visibleJoinNotifs = prefs.newUniversities ? joinNotifs : [];

  const unreadCount = visibleNotifs.filter(n => !readIds.has(n.id)).length + visibleJoinNotifs.length;


  /* ── Fetch student notifications ───────────────────────────── */
  const fetchNotifs = useCallback(async (force = false) => {
    if (!user?.universityId) return;

    const cacheHit = _cache.data
      && _cache.universityId === user.universityId
      && (Date.now() - _cache.fetchedAt) < CACHE_TTL;

    if (!force && cacheHit) {
      setNotifs(_cache.data);
      return;
    }

    if (open) setFetching(true);
    try {
      const [docsRes, studentsRes] = await Promise.all([
        supabase
          .from('documents')
          .select('id, doc_name, uploaded_at, students(id, name, type)')
          .eq('university_id', user.universityId)
          .eq('review_status', 'pending')
          .order('uploaded_at', { ascending: false })
          .limit(50),

        supabase
          .from('students')
          .select('id, name, type, is_urgent, deadline, learning_agreement_status, status, destination_university, home_university')
          .eq('university_id', user.universityId),
      ]);

      const sorted = buildNotifs(docsRes.data, studentsRes.data);
      _cache.data         = sorted;
      _cache.universityId = user.universityId;
      _cache.fetchedAt    = Date.now();
      setNotifs(sorted);
    } catch (err) {
      console.error('[NotificationBell] fetch failed:', err);
    } finally {
      setFetching(false);
    }
  }, [user?.universityId, open]);


  /* ── Fetch join notifications ──────────────────────────────── */
  const fetchJoinNotifs = useCallback(async () => {
    if (!user?.universityId) return;
    try {
      const { data } = await supabase
        .from('university_join_notifications')
        .select('id, new_uni_id, new_uni_name, new_uni_country, new_uni_abbr, created_at')
        .eq('university_id', user.universityId)
        .is('dismissed_at', null)
        .order('created_at', { ascending: false });
      setJoinNotifs(data ?? []);
    } catch (err) {
      console.error('[NotificationBell] fetchJoinNotifs:', err);
    }
  }, [user?.universityId]);


  /* ── On mount ──────────────────────────────────────────────── */
  useEffect(() => { fetchNotifs();      }, [fetchNotifs]);
  useEffect(() => { fetchJoinNotifs();  }, [fetchJoinNotifs]);

  /* ── On open: refresh both ─────────────────────────────────── */
  useEffect(() => {
    if (open) { fetchNotifs(true); fetchJoinNotifs(); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Background refresh every 60 s ────────────────────────── */
  useEffect(() => {
    if (!user?.universityId) return;
    const id = setInterval(() => { fetchNotifs(true); fetchJoinNotifs(); }, 60_000);
    return () => clearInterval(id);
  }, [fetchNotifs, fetchJoinNotifs, user?.universityId]);

  /* ── Hover handlers ────────────────────────────────────────── */
  const handleMouseEnter = () => {
    clearTimeout(leaveTimer.current);
    setOpen(true);
  };
  const handleMouseLeave = () => {
    leaveTimer.current = setTimeout(() => setOpen(false), 150);
  };


  /* ── Dismiss a join notification ───────────────────────────── */
  const dismissJoin = async (id) => {
    await supabase
      .from('university_join_notifications')
      .update({ dismissed_at: new Date().toISOString() })
      .eq('id', id);
    setJoinNotifs(prev => prev.filter(n => n.id !== id));
  };


  /* ── Handle Connect click ──────────────────────────────────── */
  // Check for an existing manual partner name-matching the new uni.
  // If found → ask to link. If not → add directly as Connected.
  const handleConnectClick = async (jn) => {
    if (connecting) return;
    setConnecting(true);
    try {
      // Find the most significant word in the university name (> 3 chars)
      const firstWord = jn.new_uni_name.split(' ').find(w => w.length > 3)
        ?? jn.new_uni_name.split(' ')[0];

      const { data: matches } = await supabase
        .from('partner_universities')
        .select('id, name, connection, linked_university_id')
        .eq('university_id', user.universityId)
        .ilike('name', `%${firstWord}%`)
        .limit(5);

      // Already linked to this university → just dismiss
      const alreadyLinked = matches?.find(p => p.linked_university_id === jn.new_uni_id);
      if (alreadyLinked) {
        await dismissJoin(jn.id);
        return;
      }

      // Manual partner with similar name → ask to link
      const manualMatch = matches?.find(p => p.connection !== 'Connected');
      setPendingConnect({ notif: jn, match: manualMatch ?? null });
    } catch (err) {
      console.error('[NotificationBell] handleConnectClick:', err);
    } finally {
      setConnecting(false);
    }
  };


  /* ── Confirm connect (after user approves) ─────────────────── */
  const confirmConnect = async () => {
    if (!pendingConnect) return;
    const { notif, match } = pendingConnect;
    setConnecting(true);
    try {
      if (match) {
        // Link an existing manual partner → set linked_university_id + Connected
        await supabase
          .from('partner_universities')
          .update({
            linked_university_id: notif.new_uni_id,
            connection:           'Connected',
          })
          .eq('id', match.id);
      } else {
        // Create a new Connected partner from scratch
        const abbr = notif.new_uni_abbr
          ?? notif.new_uni_name
               .split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase();
        await supabase
          .from('partner_universities')
          .insert({
            university_id:        user.universityId,
            name:                 notif.new_uni_name,
            abbreviation:         abbr,
            country:              notif.new_uni_country ?? '',
            type:                 'BILATERAL',
            connection:           'Connected',
            linked_university_id: notif.new_uni_id,
            max_students:         10,
          });
      }
      await dismissJoin(notif.id);
      setPendingConnect(null);
    } catch (err) {
      console.error('[NotificationBell] confirmConnect:', err);
    } finally {
      setConnecting(false);
    }
  };


  /* ── Student notification handlers ─────────────────────────── */
  const markAllRead = () => {
    const updated = new Set(notifs.map(n => n.id));
    setReadIds(updated); saveRead(updated);
  };

  const handleClick = (notif) => {
    const updated = new Set([...readIds, notif.id]);
    setReadIds(updated); saveRead(updated);
    setOpen(false); // close on click so navigation feels snappy
    navigate(notif.route);
  };

  const isEmpty = visibleJoinNotifs.length === 0 && visibleNotifs.length === 0;


  return (
    <>
      {/* ── Connect confirmation modal (portaled to body) ───────── */}
      {pendingConnect && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={e => { if (e.target === e.currentTarget && !connecting) setPendingConnect(null); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-[18px] text-emerald-600">school</span>
              </div>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">
                Connect with {pendingConnect.notif.new_uni_name}
              </h2>
            </div>
            <div className="px-6 py-5 space-y-3">
              {pendingConnect.match ? (
                <>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    You already have <span className="font-semibold text-[#0b1c30]">{pendingConnect.match.name}</span> as a manual partner. Link it to {pendingConnect.notif.new_uni_name} on Unixplore?
                  </p>
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 text-xs text-indigo-700 leading-relaxed">
                    Their profile will sync automatically. Your manually entered data is kept until they fill in their own.
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Add <span className="font-semibold text-[#0b1c30]">{pendingConnect.notif.new_uni_name}</span> as a new Connected partner? Their profile will sync automatically.
                  </p>
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 text-xs text-indigo-700 leading-relaxed">
                    You can edit the partnership details (type, max students, etc.) from the partner profile later.
                  </div>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                onClick={() => setPendingConnect(null)}
                disabled={connecting}
                className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmConnect}
                disabled={connecting}
                className="px-5 py-2.5 text-sm font-medium rounded-xl bg-[#4343d5] text-white hover:bg-indigo-700 transition-all flex items-center gap-2 disabled:opacity-60"
              >
                <span className="material-symbols-outlined text-[16px]">
                  {connecting ? 'hourglass_empty' : 'link'}
                </span>
                {connecting ? 'Connecting…' : pendingConnect.match ? 'Link Partner' : 'Add Partner'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Bell button + dropdown ──────────────────────────────── */}
      <div
        ref={ref}
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >

        {/* Bell */}
        <button
          className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors relative"
        >
          <span className="material-symbols-outlined text-[20px]">notifications</span>
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-[10px] font-bold text-white px-0.5">{unreadCount > 9 ? '9+' : unreadCount}</span>
            </span>
          )}
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute right-0 top-11 w-80 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50">

            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-sm font-semibold text-[#0b1c30]">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="text-[10px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded-full">{unreadCount}</span>
                )}
              </div>
              {visibleNotifs.filter(n => !readIds.has(n.id)).length > 0 && (
                <button onClick={markAllRead} className="text-xs text-indigo-600 font-medium hover:text-indigo-800 transition-colors">
                  Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-[28rem] overflow-y-auto divide-y divide-slate-50">
              {fetching && joinNotifs.length === 0 ? (
                [1, 2, 3].map(i => (
                  <div key={i} className="flex items-start gap-3 px-4 py-3 animate-pulse">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex-shrink-0" />
                    <div className="flex-1 space-y-1.5 pt-0.5">
                      <div className="h-3 bg-slate-100 rounded w-3/4" />
                      <div className="h-2.5 bg-slate-100 rounded w-1/2" />
                    </div>
                  </div>
                ))
              ) : isEmpty ? (
                <div className="px-4 py-8 text-center">
                  <span className="material-symbols-outlined text-slate-200 text-[36px]">notifications_off</span>
                  <p className="text-xs text-slate-400 mt-2">No notifications</p>
                </div>
              ) : (
                <>
                  {/* ── Join notifications (new universities) ── */}
                  {visibleJoinNotifs.map(jn => (
                    <div key={jn.id} className="px-4 py-3 bg-emerald-50/50 border-l-2 border-emerald-400">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="material-symbols-outlined text-[16px] text-emerald-600">school</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold leading-snug text-[#0b1c30]">
                            {jn.new_uni_name} joined Unixplore
                          </p>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            {jn.new_uni_country} · Connect to sync their profile
                          </p>
                        </div>
                        {/* Dismiss × */}
                        <button
                          onClick={() => dismissJoin(jn.id)}
                          className="text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 mt-0.5"
                        >
                          <span className="material-symbols-outlined text-[16px]">close</span>
                        </button>
                      </div>
                      {/* Connect button */}
                      <div className="mt-2.5 ml-11">
                        <button
                          onClick={() => handleConnectClick(jn)}
                          disabled={connecting}
                          className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <span className="material-symbols-outlined text-[14px]">link</span>
                          Connect
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* ── Student notifications ── */}
                  {visibleNotifs.map(notif => {
                    const isRead = readIds.has(notif.id);
                    return (
                      <button
                        key={notif.id}
                        onClick={() => handleClick(notif)}
                        className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors ${!isRead ? 'bg-indigo-50/40' : ''}`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${notif.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <span className={`material-symbols-outlined text-[16px] ${notif.iconColor}`}>{notif.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs leading-snug text-[#0b1c30] ${!isRead ? 'font-bold' : 'font-medium'}`}>
                            {notif.title}
                          </p>
                          <p className="text-[11px] text-slate-400 mt-0.5 truncate">{notif.desc}</p>
                        </div>
                        {!isRead && <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-1.5" />}
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between">
              <p className="text-[11px] text-slate-400">
                {fetching ? 'Loading…' : unreadCount === 0 ? 'All caught up!' : `${unreadCount} unread`}
              </p>
              {!fetching && (
                <button
                  onClick={() => { fetchNotifs(true); fetchJoinNotifs(); }}

                  className="text-[11px] text-slate-400 hover:text-indigo-600 transition-colors flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-[13px]">refresh</span>Refresh
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
